import { computed, inject } from '@angular/core';
import { AppLog } from '@app/core/services';
import { ConsortiumService } from '@app/core/services/backend/mci/consortium.service';
import {
    IConsortiumAgency,
    IConsortiumApprovalResponse,
    IConsortiumMembershipResponse,
    IUpdateApprovalStatusResponse,
} from '@models/consortium/consortium.interface';
import { LogPriority } from '@app/core/services/log/log-priority.enum';
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { lastValueFrom } from 'rxjs';
import { UsersStore } from './users.store';
import { AppPermissions } from '@app/core/services/acl/config/acl-permissions-constants';

const MEMBERSHIP_STORAGE_KEY = 'consortium-membership';

type ConsortiumState = {
    requests: IConsortiumApprovalResponse[] | null;
    membership: IConsortiumMembershipResponse | null;
    loading: boolean;
};

const initialState: ConsortiumState = {
    requests: null,
    membership: null,
    loading: false,
};

export const ConsortiumStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withComputed((store) => ({
        isConsortiumLoaded: computed<boolean>(() => store.requests() !== null),
        isMembershipLoaded: computed<boolean>(() => store.membership() !== null),
        isConsortiumMember: computed<boolean>(() => (store.membership()?.consortiumMemberCount ?? 0) > 0),
        leadAgencyName: computed<string>(() => {
            const membership = store.membership();
            if (!membership?.agencies?.length || !membership.leadAgencyGcId) {
                return '-';
            }
            const lead = membership.agencies.find((a) => a.gcId === membership.leadAgencyGcId);
            return lead?.name ?? '-';
        }),
        contributingAgencies: computed<IConsortiumAgency[]>(() => {
            const membership = store.membership();
            if (!membership?.agencies?.length) {
                return [];
            }
            const leadGcId = membership.leadAgencyGcId;
            return [...membership.agencies].sort((a, b) => {
                if (a.gcId === leadGcId) return -1;
                if (b.gcId === leadGcId) return 1;
                return 0;
            });
        }),
    })),
    withProps(() => ({
        consortiumService: inject(ConsortiumService),
        logger: inject(AppLog),
        usersStore: inject(UsersStore),
    })),
    withComputed((store) => {
        /**
         * Backward-compatible warning source:
         * - Prefer backend `has_approver` when present on all agencies.
         * - Fallback to legacy role-based check from UsersStore otherwise.
         */
        const agenciesWithoutApprover = computed<IConsortiumAgency[] | null>(() => {
            const membership = store.membership();
            if (!membership?.agencies?.length) {
                return [];
            }

            const agencies = membership.agencies;
            const hasBackendApproverFlags = agencies.every((agency) => agency.has_approver !== undefined);

            if (hasBackendApproverFlags) {
                return agencies.filter((agency) => agency.has_approver === 0);
            }

            if (!store.usersStore.isUserLoaded()) {
                return null;
            }

            const users = store.usersStore.entities();
            return agencies.filter((agency) => {
                const agencyUsers = users.filter((u) => u.accountIds?.includes(agency.gcId));
                return !agencyUsers.some((u) => u.roles.some((r) => r.code === AppPermissions.mci.consortiumApprover));
            });
        });

        return {
            agenciesWithoutApprover,
            /**
             * Whether any agency lacks an approver.
             */
            hasApproverWarnings: computed<boolean>(() => {
                const agencies = agenciesWithoutApprover();
                return agencies !== null && agencies.length > 0;
            }),
        };
    }),
    withMethods(({ consortiumService, logger, ...store }) => ({
        async loadApprovalRequests(gcId: string): Promise<void> {
            if (store.isConsortiumLoaded()) {
                return;
            }

            patchState(store, { loading: true });
            try {
                const requests = await lastValueFrom(consortiumService.getApprovalRequests(gcId));
                logger.debug('Consortium approval requests loaded successfully', { logPriority: LogPriority.LOW });
                patchState(store, { requests, loading: false });
            } catch (error) {
                logger.error('Error loading consortium approval requests:', { logPriority: LogPriority.HIGH }, error);
                patchState(store, { loading: false });
            }
        },

        async loadMembership(): Promise<void> {
            const cached = localStorage.getItem(MEMBERSHIP_STORAGE_KEY);
            if (cached) {
                try {
                    const membership: IConsortiumMembershipResponse = JSON.parse(cached);
                    patchState(store, { membership });
                    return;
                } catch {
                    localStorage.removeItem(MEMBERSHIP_STORAGE_KEY);
                }
            }

            try {
                const membership = await lastValueFrom(consortiumService.getMembership());
                patchState(store, { membership });
                localStorage.setItem(MEMBERSHIP_STORAGE_KEY, JSON.stringify(membership));
            } catch (error) {
                logger.error('Error loading consortium membership:', { logPriority: LogPriority.HIGH }, error);
                const membership: IConsortiumMembershipResponse = { consortiumMemberCount: 0 };
                patchState(store, { membership });
                localStorage.setItem(MEMBERSHIP_STORAGE_KEY, JSON.stringify(membership));
            }
        },

        clearMembership(): void {
            localStorage.removeItem(MEMBERSHIP_STORAGE_KEY);
            patchState(store, { membership: null });
        },

        resolveAgencyName(gcId: string): string {
            const agencies = store.contributingAgencies();
            const agency = agencies.find((a) => String(a.gcId) === gcId);
            return agency?.name ?? '';
        },

        async cancelApprovalRequest(requestId: number, taskId: string, gcId: string): Promise<boolean> {
            try {
                const response = await lastValueFrom(
                    consortiumService.cancelApprovalRequest(requestId, { taskId, gcId }),
                );
                if (!response) return false;
                this.updateRequestInStore(response);
                return true;
            } catch (error) {
                logger.error('Error cancelling approval request:', { logPriority: LogPriority.HIGH }, error);
                return false;
            }
        },

        async updateApprovalStatus(
            requestId: number,
            taskStatus: string,
            taskId: string,
            gcId: string,
        ): Promise<IUpdateApprovalStatusResponse | null> {
            try {
                const response = await lastValueFrom(
                    consortiumService.updateApprovalStatus(requestId, taskStatus, { taskId, gcId }),
                );
                if (!response) return null;
                this.updateRequestInStore(response.approvalRequest);
                return response;
            } catch (error) {
                logger.error('Error updating approval status:', { logPriority: LogPriority.HIGH }, error);
                return null;
            }
        },

        updateRequestInStore(updatedRequest: IConsortiumApprovalResponse): void {
            const requests = store.requests();
            if (!requests) return;
            const updated = requests.map((r) => (r.requestId === updatedRequest.requestId ? updatedRequest : r));
            patchState(store, { requests: updated });
        },
    })),
);
